import {api} from '../../contexts/AuthContext';

// Scheduled-jobs API (admin-gated). Minimal summary shape needed by the
// Blueprints table's "Connected Jobs" column — the full job model lives in
// ScheduledJobs.tsx. We only consume id/name/promptIds/isActive/nextRunAt/endDate here.
export interface ScheduledJobSummary {
    id: string;
    name: string;
    promptIds: string[];
    isActive: boolean;
    nextRunAt?: string;
    // Past end date means the job no longer runs even if isActive is still
    // true (deepcontext-ve6o) — the Connected Jobs dot treats it as dormant.
    endDate?: string | null;
    // Sum of urls_processed across all of this job's runs (deepcontext-jvqwp),
    // used by the Blueprints "Est. cost" column. `null` when the job has zero
    // runs (render an em dash, not $0.00); a number (incl. 0) when it has run.
    totalUrlsProcessed?: number | null;
}

// Blueprint-usage map (bd-n3yqb / one-job-per-blueprint, bd-440mr). Keyed by
// promptId, each entry names the scheduled job that currently owns that
// blueprint so the picker can disable it and show "Already used by …".
// Date refinement (ignoring jobs older than the blueprint) is enforced
// backend-side — the client just renders whatever map is returned.
export type BlueprintUsageMap = Record<string, { jobId: string; jobName: string }>;

export const scheduledJobApi = {
    // GET /api/scheduled-jobs is admin-only; callers must gate by role before
    // calling. Returns the full job list which we filter client-side by
    // job.promptIds.includes(promptId).
    getAll: async (): Promise<ScheduledJobSummary[]> => {
        const response = await api.get('/api/scheduled-jobs');
        return response.data;
    },

    // Which scheduled job (if any) owns each blueprint. Pass `promptIds` to
    // scope the lookup, or omit to get usage across every blueprint. Admin-only.
    getBlueprintUsage: async (promptIds?: string[]): Promise<BlueprintUsageMap> => {
        const params = new URLSearchParams();
        if (promptIds && promptIds.length > 0) {
            params.set('promptIds', promptIds.join(','));
        }
        const queryString = params.toString();
        const response = await api.get(
            `/api/scheduled-jobs/blueprint-usage${queryString ? `?${queryString}` : ''}`
        );
        return response.data;
    },
};
